// Map props: free-standing objects placed on a Tiled object layer (a palm,
// a hut, a crate). The sim only needs their footprint: which cells they
// block and whether they stop bullets. How a prop is drawn (crown above
// the soldiers, trunk below, shadow, sway) is the client's business and
// keyed by the same name. A prop's object in Tiled carries a tile gid
// whose tile class names the kind here (tools/jungle2tmj writes it).

export interface PropKind {
  readonly name: string;
  // Blocked cells, centred on the object's image centre. 0 = walk through.
  readonly footW: number;
  readonly footH: number;
  readonly opaque: boolean; // the footprint also stops bullets and sight
  // 'over': drawn above the soldiers (a canopy you walk under, a bush you
  // hide in); 'ground': drawn below them.
  readonly layer: 'ground' | 'over';
}

export const PROP_KINDS: readonly PropKind[] = [
  // One cell of fortification wall (bamboo palisade): the jungle's brick.
  // Blocks movement and bullets like arena01's walls; lines bridge sides.
  { name: 'wall', footW: 1, footH: 1, opaque: true, layer: 'ground' },
  // The crown is drawn above the soldiers; the trunk (one cell) is what
  // you bump into, so you walk under the fronds and behind the tree.
  { name: 'palm', footW: 1, footH: 1, opaque: true, layer: 'over' },
  // Bamboo hut: a 5x5-cell roof over a 4x4 solid body, so the eaves hang
  // over a soldier walking along the wall.
  { name: 'hut', footW: 4, footH: 4, opaque: true, layer: 'over' },
  { name: 'boulder', footW: 2, footH: 2, opaque: true, layer: 'ground' },
  { name: 'rocks', footW: 0, footH: 0, opaque: false, layer: 'ground' }, // decoration, stepped over
  { name: 'bush', footW: 0, footH: 0, opaque: false, layer: 'over' }, // hides a soldier, stops nothing
  { name: 'crate', footW: 2, footH: 2, opaque: true, layer: 'ground' },
  { name: 'log', footW: 2, footH: 1, opaque: false, layer: 'ground' }, // low cover: blocks feet, not bullets
  { name: 'log-v', footW: 1, footH: 2, opaque: false, layer: 'ground' },
  { name: 'sandbags', footW: 3, footH: 1, opaque: false, layer: 'ground' }, // shoot over, do not cross
  { name: 'sandbags-v', footW: 1, footH: 3, opaque: false, layer: 'ground' },
];

export function propKind(name: string): PropKind | undefined {
  for (const k of PROP_KINDS) if (k.name === name) return k;
  return undefined;
}

// A placed prop as the level lists it: image centre in pixels, and the
// tile image Tiled draws it with (relative to the level file; the client
// loads every level image under its path as the texture key).
export interface LevelProp {
  readonly kind: string;
  readonly x: number;
  readonly y: number;
  readonly image?: string;
}
